import { create } from 'zustand';
import request from 'utils/request';


export type CallDetails = Record<string, unknown>;

export type InitiateOutcome = 'sent' | 'unavailable' | 'empty' | 'cleared';

export interface CallStoreState {
  callInitiateDetails?: CallDetails;
  callIncomingDetails?: CallDetails;
  callOutgoingDetails?: CallDetails;
  callActiveDetails?: CallDetails;

  setIncoming: (details?: CallDetails) => void;
  setOutgoing: (details?: CallDetails) => void;

  initiate: (
    details: { users: CallDetails[]; devices: CallDetails[] } | undefined,
    authToken: string,
    entityId: number | string
  ) => Promise<InitiateOutcome>;
  answer: (
    incomingDetails: CallDetails | undefined,
    authToken: string
  ) => Promise<void>;
  activate: (
    details: CallDetails | undefined,
    authToken: string
  ) => Promise<void>;
  abort: (details: CallDetails | undefined, authToken: string) => Promise<void>;
  end: (details: CallDetails | undefined, authToken: string) => Promise<void>;

  reset: () => void;
}

const CALL_ENDPOINT = '/api/messaging/call';
const TELEMETRY_ENDPOINT = '/api/telemetry';

const jsonHeaders = (authToken: string) => ({
  'Content-Type': 'application/json',
  authorization: authToken,
});

export const useCallStore = create<CallStoreState>((set, get) => ({
  callInitiateDetails: undefined,
  callIncomingDetails: undefined,
  callOutgoingDetails: undefined,
  callActiveDetails: undefined,

  setIncoming: (details) => set({ callIncomingDetails: details }),
  setOutgoing: (details) => set({ callOutgoingDetails: details }),

  initiate: async (details, authToken, entityId) => {
    set({ callInitiateDetails: details });
    if (!details) return 'cleared';

    const users = (details.users || []).map((user) => user.userName);
    const devices = (details.devices || []).map((device) => device.deviceId);
    if (users.length === 0 && devices.length === 0) {
      set({ callOutgoingDetails: undefined });
      return 'empty';
    }

    try {
      const result = await request<{
        success?: boolean;
        callDetails?: CallDetails;
      }>(CALL_ENDPOINT, {
        method: 'POST',
        body: JSON.stringify({
          action: 'initiate',
          entityId,
          callDetails: { targets: { users, devices } },
        }),
        headers: jsonHeaders(authToken),
      });
      if (result?.success === true) {
        set({ callOutgoingDetails: result.callDetails });
        return 'sent';
      }
      set({ callInitiateDetails: undefined });
      return 'unavailable';
    } catch (err) {
      console.error('Error initiating call:', err);
      set({ callInitiateDetails: undefined });
      return 'unavailable';
    }
  },

  answer: async (incomingDetails, authToken) => {
    if (!incomingDetails) return;
    try {
      const result = await request<{ success?: boolean }>(CALL_ENDPOINT, {
        method: 'POST',
        body: JSON.stringify({
          action: 'targetAnswer',
          callDetails: incomingDetails,
        }),
        headers: jsonHeaders(authToken),
      });
      if (result?.success === true) {
        await get().activate(incomingDetails, authToken);
      }
    } catch (err) {
      console.error('Error answering call:', err);
    }
  },

  activate: async (details, authToken) => {
    set({ callActiveDetails: details });
    if (!details) return;
    try {
      await request(TELEMETRY_ENDPOINT, {
        method: 'POST',
        body: JSON.stringify({ telemetryDescription: 'Support Call' }),
        headers: jsonHeaders(authToken),
      });
    } catch (err) {
      console.error('Error recording call telemetry:', err);
    }
  },

  abort: async (details, authToken) => {
    if (!details) return;
    try {
      await request(CALL_ENDPOINT, {
        method: 'POST',
        body: JSON.stringify({ action: 'sourceAbort', callDetails: details }),
        headers: jsonHeaders(authToken),
      });
    } catch (err) {
      console.error('Error aborting call:', err);
    }
  },

  end: async (details, authToken) => {
    if (!details) return;
    try {
      await request(CALL_ENDPOINT, {
        method: 'POST',
        body: JSON.stringify({ action: 'sourceEnd', callDetails: details }),
        headers: jsonHeaders(authToken),
      });
    } catch (err) {
      console.error('Error ending call:', err);
    }
  },

  reset: () =>
    set({
      callInitiateDetails: undefined,
      callIncomingDetails: undefined,
      callOutgoingDetails: undefined,
      callActiveDetails: undefined,
    }),
}));
